import React from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Phone, Star, Shield, Clock } from "lucide-react";
import { trackLead, trackViewContent } from "./utils/tracking";

const M = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const trustPoints = [
  "500+ couples helped across 15+ countries",
  "Team of 11 expert doctors & nutritionists",
  "Evidence-based, personalized nutrition plans",
  "Rated 4.9/5 by 200+ clients",
];

const results = [
  { stat: "78%", label: "of clients see cycle regulation within 3 months" },
  { stat: "3x", label: "improvement in egg quality markers" },
  { stat: "92%", label: "client satisfaction rate" },
];

const features = [
  { icon: Shield, title: "Doctor-Backed", desc: "Plans designed with gynecologists & endocrinologists" },
  { icon: Clock, title: "90-Day Programs", desc: "Structured plans aligned with your egg maturation cycle" },
  { icon: Phone, title: "Weekly Follow-ups", desc: "Regular check-ins to track progress and adjust" },
];

export default function PCOSLandingPage() {
  React.useEffect(() => {
    trackViewContent({ content_name: "PCOS Landing Page", content_type: "ad_landing" });
  }, []);

  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    const msg = `Hi! I'm interested in the PCOS Nutrition Program.\n\nName: ${name}\nPhone: ${phone}`;
    window.open(`https://wa.me/919108818369?text=${encodeURIComponent(msg)}`, "_blank");
    trackLead({ content_name: "PCOS Ad Landing", value: 0 });
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="pcos-landing">
      <Helmet>
        <title>PCOS Nutrition Program | Fitly India — Expert Fertility Nutritionists</title>
        <meta name="description" content="Struggling with PCOS and fertility? Get a personalized nutrition plan from India's leading fertility nutritionists. 500+ couples helped. Book your free 15-min call." />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Hero — Single focused CTA */}
      <section className="bg-emerald_deep relative overflow-hidden" data-testid="pcos-hero">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Star size={14} className="text-accent fill-accent" />
                <span className="text-accent text-xs tracking-[0.15em] uppercase font-bold">Rated 4.9/5 by 200+ Clients</span>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bone leading-tight tracking-tight">
                PCOS Doesn't Have to <span className="text-accent italic">Define Your Journey</span>
              </h1>
              <p className="text-bone/70 text-base md:text-lg mt-6 leading-relaxed max-w-lg">
                Get a personalized nutrition plan from India's leading fertility nutritionists. Manage PCOS, regulate cycles, and improve your conception chances — naturally.
              </p>

              {/* Trust points */}
              <ul className="mt-8 space-y-3">
                {trustPoints.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-bone/80 text-sm">
                    <Check size={16} className="text-accent flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>

              {/* Mobile CTA */}
              <div className="lg:hidden mt-8">
                <a href="#pcos-form" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-10 py-4 text-sm tracking-[0.1em] uppercase font-bold rounded-full w-full justify-center">
                  Get Your Free Call <ArrowRight size={16} />
                </a>
              </div>
            </div>

            {/* Form — Desktop */}
            <div className="hidden lg:block" id="pcos-form-desktop">
              <div className="bg-bone rounded-2xl p-8 shadow-2xl" data-testid="pcos-form-desktop">
                <h2 className="font-serif text-2xl text-emerald_deep italic tracking-tight mb-2">
                  Free 15-Minute Discovery Call
                </h2>
                <p className="text-text_muted text-sm mb-6">No commitment. Understand how nutrition can help your PCOS journey.</p>
                {submitted ? (
                  <div className="text-center py-8" data-testid="pcos-form-success">
                    <div className="w-14 h-14 rounded-full bg-emerald_deep/10 flex items-center justify-center mx-auto mb-3">
                      <Check size={24} className="text-emerald_deep" />
                    </div>
                    <p className="font-serif text-xl text-emerald_deep italic">We'll call you shortly!</p>
                    <p className="text-text_muted text-sm mt-2">Redirecting to WhatsApp...</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="pcos-name" className="w-full border border-border_custom px-4 py-3 text-text_main placeholder-text_muted/50 focus:border-emerald_deep focus:outline-none rounded-lg text-sm" />
                    <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="pcos-phone" className="w-full border border-border_custom px-4 py-3 text-text_main placeholder-text_muted/50 focus:border-emerald_deep focus:outline-none rounded-lg text-sm" />
                    <button type="submit" data-testid="pcos-submit" className="w-full bg-accent text-emerald_deep py-4 text-sm tracking-[0.1em] uppercase font-bold hover:bg-accent_light transition-colors rounded-full">
                      Claim My Free Call
                    </button>
                    <p className="text-center text-text_muted text-[11px]">We'll reach you on WhatsApp within 2 hours.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results strip */}
      <section className="bg-accent/10 py-10" data-testid="pcos-results">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            {results.map((r) => (
              <div key={r.stat}>
                <p className="font-serif text-3xl md:text-4xl text-emerald_deep italic tracking-tight">{r.stat}</p>
                <p className="text-text_muted text-xs md:text-sm mt-1">{r.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-16 md:py-24" data-testid="pcos-features">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-emerald_deep italic tracking-tight text-center mb-12">
            What You Get With Fitly
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald_deep/5 flex items-center justify-center mx-auto mb-4">
                  <Icon size={22} className="text-accent" />
                </div>
                <h3 className="font-serif text-xl text-emerald_deep italic mb-2">{title}</h3>
                <p className="text-text_muted text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder trust */}
      <section className="bg-emerald_deep py-16" data-testid="pcos-founder">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-accent/25 mx-auto mb-6">
            <img src={`${M}/nadiya-founder.webp`} alt="Nadiya" className="w-full h-full object-cover object-top" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-bone italic tracking-tight leading-snug max-w-2xl mx-auto">
            "I've helped 500+ couples manage PCOS through nutrition. Let me understand your story — it costs nothing to have a conversation."
          </p>
          <p className="text-bone/40 text-xs tracking-[0.15em] uppercase font-semibold mt-4">— Nadiya, Founder of Fitly India</p>
        </div>
      </section>

      {/* Mobile form */}
      <section className="lg:hidden py-12 bg-surface_alt" id="pcos-form" data-testid="pcos-form-mobile">
        <div className="max-w-md mx-auto px-6">
          <h2 className="font-serif text-2xl text-emerald_deep italic tracking-tight text-center mb-2">
            Free 15-Minute Discovery Call
          </h2>
          <p className="text-text_muted text-sm text-center mb-6">No commitment needed.</p>
          {submitted ? (
            <div className="text-center py-8">
              <Check size={32} className="text-emerald_deep mx-auto mb-3" />
              <p className="font-serif text-xl text-emerald_deep italic">We'll call you shortly!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} required className="w-full border border-border_custom px-4 py-3 text-text_main placeholder-text_muted/50 focus:border-emerald_deep focus:outline-none rounded-lg text-sm bg-white" />
              <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} required className="w-full border border-border_custom px-4 py-3 text-text_main placeholder-text_muted/50 focus:border-emerald_deep focus:outline-none rounded-lg text-sm bg-white" />
              <button type="submit" className="w-full bg-accent text-emerald_deep py-4 text-sm tracking-[0.1em] uppercase font-bold hover:bg-accent_light transition-colors rounded-full">
                Claim My Free Call
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="bg-emerald_deep/95 py-8 text-center">
        <p className="text-bone/40 text-xs">&copy; {new Date().getFullYear()} Fitly India. All rights reserved.</p>
      </footer>
    </div>
  );
}
